import { Calendar, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import { Card, CardContent } from '@/components/ui/card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { periodManagementService } from '@/services/period-management-service';
import { useIntegration } from '@/providers/IntegrationProvider';
import type { PeriodSettings } from '@/types/settings';

export function KpiCurrentPeriod() {
  const [periodSettings, setPeriodSettings] = useState<PeriodSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { settings: integrationSettings } = useIntegration();
  const navigate = useNavigate();

  const hasFinancialsIntegration = integrationSettings?.enabled && 
    integrationSettings?.integrationType === 'access-financials';
  
  const isSynced = hasFinancialsIntegration && periodSettings?.lastSyncedAt;

  useEffect(() => {
    const fetchPeriodSettings = async () => {
      setIsLoading(true);
      try {
        const settings = await periodManagementService.getPeriodSettings();
        setPeriodSettings(settings);
      } catch (error) {
        console.error('Error fetching period settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPeriodSettings();
  }, []);

  if (isLoading) {
    return (
      <Card className="overflow-hidden border-0">
        <CardContent className="p-6 h-[148px] flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground mt-2">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  const periodValue = periodSettings 
    ? `${String(periodSettings.currentYear).padStart(4, '0')}/${String(periodSettings.currentPeriod).padStart(2, '0')}`
    : 'N/A';

const getSubheading = (): string => {
    if (!hasFinancialsIntegration) return 'System Period';
    return isSynced ? 'Synced with Integration' : 'Integration Sync Required';
  };

  const getStatusIcon = () => {
    if (!hasFinancialsIntegration) return undefined;
    return isSynced ? CheckCircle2 : XCircle;
  };
  
  const subheading = getSubheading();
  const statusIcon = getStatusIcon();

  const handleClick = () => {
    navigate('/settings/period-management');
  };

  return (
    <StaticMetricCardAlt
      title="Current Period"
      value={periodValue}
      changeLabel={subheading}
      changeLabelIcon={statusIcon}
      icon={Calendar}
      variant="info"
      onClick={handleClick}
    />
  );
}
